import { Component, computed, input, output } from '@angular/core';
import type { SolarPanelsResultOverviewRendering } from '@feh/solar-panels-models';
import type { SolarPanelsProductWithCalculations } from '@feh/solar-panels-store';
import type { SolarPanelsBuildingDataSchema } from '@integration/api-client/feh';
import { ScFieldValuePipe } from '@innogy/feh/shared';
import { RichTextWrapperComponent } from '@legacy/common-ui-utility-components';
import {
  GenericLinkDirective,
  getLinkFieldValue,
  TextInterpolationPipe,
} from '@core/jss-utils';
import { CSMaskDirective } from '@legacy/utils-contentsquare';
import { JssModule } from '@sitecore-jss/sitecore-jss-angular';
import { CrntButton, CrntIcon } from '@essent/current-web-angular';
import { TrackLinkDirective } from '@analytics/events';
import {
  CommonUiServiceComponentsModule,
  FaqContainerComponent,
} from '@innogy/common-ui/service-components';
import type { GenericModalProps } from '@legacy/common-ui-shared-interfaces';
import { AutomationIdDirective } from '@innogy/utils-automation-id';

import { SolarPanelsResultOverviewMapComponent } from '../solar-panels-result-overview-map/solar-panels-result-overview-map.component';
import { SolarPanelsPropositionBoxComponent } from '../solar-panels-proposition-box/solar-panels-proposition-box.component';

@Component({
  selector: 'wl-solar-panels-result-overview-ui',
  standalone: true,
  templateUrl: './solar-panels-result-overview-ui.component.html',
  styleUrls: [
    './solar-panels-result-overview-ui.component.ed.scss',
    './solar-panels-result-overview-ui.component.essent.scss',
  ],
  imports: [
    ScFieldValuePipe,
    SolarPanelsResultOverviewMapComponent,
    SolarPanelsPropositionBoxComponent,
    RichTextWrapperComponent,
    TextInterpolationPipe,
    CSMaskDirective,
    JssModule,
    CrntIcon,
    CrntButton,
    TrackLinkDirective,
    GenericLinkDirective,
    FaqContainerComponent,
    CommonUiServiceComponentsModule,
    AutomationIdDirective,
  ],
})
export class SolarPanelsResultOverviewUiComponent {
  readonly rendering = input.required<SolarPanelsResultOverviewRendering>();
  readonly address = input.required<string>();
  readonly building = input<SolarPanelsBuildingDataSchema>();
  readonly product = input<SolarPanelsProductWithCalculations>();

  submitted = output<void>();
  openPropositionModal = output<GenericModalProps>();

  readonly faqRendering = computed(() => {
    const buttonText = this.rendering().fields.MoreInfoButtonText?.value;
    const descriptionText =
      this.rendering().fields.MoreInfoDescriptionText?.value;

    if (buttonText && descriptionText) {
      return {
        componentName: this.rendering().componentName,
        fields: {
          Items: [
            {
              name: 'Items',
              fields: {
                question: {
                  value: buttonText,
                },
                answer: {
                  value: descriptionText,
                },
              },
            },
          ],
        },
      };
    }
    return undefined;
  });

  onClickSubmitButton(product?: SolarPanelsProductWithCalculations) {
    if (!product) {
      throw new Error(
        'Attempted to submit solar overview without providing a product.'
      );
    }
    this.submitted.emit();
  }

  onOpenPropositionMoreInfoModal(props: GenericModalProps) {
    this.openPropositionModal.emit(props);
  }

  get secondaryButtonLinkValue() {
    return getLinkFieldValue(this.rendering().fields, 'SecondaryButtonLink');
  }
}
